import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import PPUtils from "../../utils/PPUtils"

function cropToDataUrl(img, rect, size = 300) {
    const canvas = document.createElement("canvas")
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext("2d")
    ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height, 0, 0, size, size)
    return canvas.toDataURL("image/png")
}

export function AvatarEditor({ avatarState, name }) {
    const [t] = useTranslation()
    const [preview, setPreview] = useState("")
    const [imgSrc, setImgSrc] = useState(null)
    const [rect, setRect] = useState(null)
    const [avatar, setAvatar] = avatarState
    const imgRef = useRef(null)
    const dragStart = useRef(null)
    const [dragging, setDragging] = useState(false)

    function onFileChange(e) {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
            setImgSrc(reader.result)
            setPreview("")
            setRect(null)
        }
        reader.readAsDataURL(file)
    }

    function getRect() {
        if (rect) return rect
        const img = imgRef.current
        if (!img) return null
        const side = Math.min(img.naturalWidth, img.naturalHeight)
        return {
            x: (img.naturalWidth - side) / 2,
            y: (img.naturalHeight - side) / 2,
            width: side,
            height: side
        }
    }

    function applyCrop() {
        const img = imgRef.current
        if (!img) return
        const r = getRect()
        setPreview(cropToDataUrl(img, r))
    }

    function onPointerDown(e) {
        const img = imgRef.current
        if (!img) return
        const bounds = img.getBoundingClientRect()
        dragStart.current = {
            x: e.clientX - bounds.left,
            y: e.clientY - bounds.top
        }
        setDragging(true)
    }

    function onPointerMove(e) {
        if (!dragging || !dragStart.current) return
        const img = imgRef.current
        if (!img) return
        const bounds = img.getBoundingClientRect()
        const scaleX = img.naturalWidth / bounds.width
        const scaleY = img.naturalHeight / bounds.height
        const cur = {
            x: (e.clientX - bounds.left) * scaleX,
            y: (e.clientY - bounds.top) * scaleY
        }
        const start = {
            x: dragStart.current.x * scaleX,
            y: dragStart.current.y * scaleY
        }
        const side = Math.max(Math.abs(cur.x - start.x), Math.abs(cur.y - start.y))
        setRect({
            x: Math.min(start.x, cur.x),
            y: Math.min(start.y, cur.y),
            width: side,
            height: side
        })
    }

    function onPointerUp() {
        setDragging(false)
        applyCrop()
    }

    function rectStyle() {
        const img = imgRef.current
        if (!img) return {}
        const bounds = img.getBoundingClientRect()
        const r = getRect()
        return {
            left: (r.x / img.naturalWidth) * bounds.width + "px",
            top: (r.y / img.naturalHeight) * bounds.height + "px",
            width: (r.width / img.naturalWidth) * bounds.width + "px",
            height: (r.height / img.naturalHeight) * bounds.height + "px"
        }
    }

    return (
        <>
            <div className="pp-user-settings-avatar">
                {avatar ? (
                    <img src={avatar} className="rounded-circle elevation-2" width="150" height="150" alt="" />
                ) : (
                    PPUtils.getDummyAvatarString(name, 150)
                )}
            </div>
            <button
                className="btn btn-primary btn-sm pp-user-settings-aside-btn"
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#avatarEditorModalCenter">
                {t("single.change_avatar_editor")}
            </button>
            <button
                className="btn btn-primary btn-sm pp-user-settings-aside-btn"
                type="button"
                onClick={() => {
                    setPreview("")
                    setAvatar("")
                    setImgSrc(null)
                    setRect(null)
                }}>
                {t("single.change_avatar_reset")}
            </button>
            {/* Modal */}
            <div
                className="modal fade"
                id="avatarEditorModalCenter"
                tabIndex={-1}
                role="dialog"
                aria-labelledby="avatarEditorModalCenterTitle"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-centered" role="dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="avatarModalLongTitle">
                                {t("settings.usersettings.header_avatar_editor")}
                            </h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <div className="modal-body avatar-padding avatar-editor-modal-body">
                            <div className="row">
                                <div className="col-8">
                                    <input type="file" accept="image/*" className="form-control mb-2" onChange={onFileChange} />
                                    {imgSrc && (
                                        <div style={{ position: "relative", userSelect: "none" }}>
                                            <img
                                                ref={imgRef}
                                                src={imgSrc}
                                                alt=""
                                                style={{ width: "100%", height: 295, objectFit: "contain", touchAction: "none" }}
                                                onPointerDown={onPointerDown}
                                                onPointerMove={onPointerMove}
                                                onPointerUp={onPointerUp}
                                                onPointerLeave={onPointerUp}
                                                draggable={false}
                                            />
                                            {rect && (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        border: "2px dashed #3f6ad8",
                                                        pointerEvents: "none",
                                                        ...rectStyle()
                                                    }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="col-4 text-center">
                                    <h5>{t("single.preview")}</h5>
                                    {preview ? (
                                        <img src={preview} alt="" className="avatarEditorPreview rounded-circle" />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                {t("single.close")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-dismiss="modal"
                                onClick={() => setAvatar(preview)}
                            >
                                {t("single.apply_filter")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}